import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, AlertCircle, CheckCircle2, CheckCircle, Circle, Clock, ChevronDown, ChevronUp, ExternalLink } from 'lucide-react';
import Navigation from '../components/Navigation';
import { toast } from 'sonner';
import { formatDate } from '../utils/dateFormat';

import { API, API_BASE } from '../config/api';
const BACKEND_URL = API_BASE;

// Helper function to render text with clickable links - inlined to ensure deployment
const renderTextWithLinks = (text) => {
  if (!text) return null;
  
  // URL regex pattern - matches http and https URLs
  const urlPattern = /(https?:\/\/[^\s<]+[^<.,:;"')\]\s])/g;
  const parts = text.split(urlPattern);
  
  return parts.map((part, index) => {
    // Check if this part is a URL
    if (part && part.match(/^https?:\/\//)) {
      return (
        <a
          key={index}
          href={part}
          target="_blank"
          rel="noopener noreferrer"
          className="text-[#80298F] hover:underline inline-flex items-center gap-1 break-all"
          onClick={(e) => e.stopPropagation()}
        >
          {part}
          <ExternalLink className="h-3 w-3 inline-block flex-shrink-0" />
        </a>
      );
    }
    return part ? <span key={index}>{part}</span> : null;
  });
};

function MyTasks({ user }) {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Collapse states for each section
  const [overdueCollapsed, setOverdueCollapsed] = useState(false);
  const [pendingCollapsed, setPendingCollapsed] = useState(false);
  const [completedCollapsed, setCompletedCollapsed] = useState(true); // Completed collapsed by default

  const handleLogout = async () => {
    await fetch(`${API}/auth/logout`, { method: 'POST', credentials: 'include' });
    window.location.href = '/login';
  };

  useEffect(() => {
    fetchTasks();
  }, []);

  const fetchTasks = async () => {
    try {
      const response = await fetch(`${API}/tasks`, { credentials: 'include' });
      if (response.ok) {
        setTasks(await response.json());
      }
    } catch (error) {
      toast.error('Failed to load tasks');
    } finally {
      setLoading(false);
    }
  };

  const now = new Date();
  now.setHours(0, 0, 0, 0);

  const overdueTasks = tasks.filter(t => !t.completed && new Date(t.due_date) < now);
  const pendingTasks = tasks.filter(t => !t.completed && new Date(t.due_date) >= now);
  const completedTasks = tasks.filter(t => t.completed);

  const renderTask = (task) => {
    const isOverdue = !task.completed && new Date(task.due_date) < now;
    
    return (
      <div 
        key={task.task_id} 
        className={`p-4 border rounded-lg ${
          task.completed ? 'border-green-200 bg-green-50/30' :
          isOverdue ? 'border-red-300 bg-red-50/50' : 'border-gray-200'
        }`}
      >
        <div className="flex items-start gap-3">
          <div className={`mt-1 transition-all duration-200 ${task.completed ? 'text-green-600' : isOverdue ? 'text-red-500' : 'text-gray-400'}`}>
            {task.completed ? <CheckCircle2 className="h-5 w-5 fill-green-500 text-green-500" /> : <Circle className="h-5 w-5" />}
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h4 className={`font-medium ${task.completed ? 'text-gray-500 line-through' : 'text-gray-900'}`}>
                {task.title}
              </h4>
              {isOverdue && (
                <span className="flex items-center gap-1 text-xs text-red-600 font-medium">
                  <AlertCircle className="h-3 w-3" />
                  Overdue
                </span>
              )}
            </div>
            <p className={`text-sm mt-1 ${isOverdue ? 'text-red-600' : 'text-gray-500'}`}>
              Due: {formatDate(task.due_date)}
            </p>
            {task.description && (
              <div className="mt-2 text-sm text-gray-600">
                {task.description.includes('Why this is needed:') ? (
                  <>
                    <p>{renderTextWithLinks(task.description.split('Why this is needed:')[0].trim())}</p>
                    <p className="mt-2 text-gray-500">
                      <span className="font-medium text-gray-700">Why this is needed:</span>{' '}
                      <span className="italic">{renderTextWithLinks(task.description.split('Why this is needed:')[1]?.trim())}</span>
                    </p>
                  </>
                ) : (
                  <p>{renderTextWithLinks(task.description)}</p>
                )}
              </div>
            )}
          </div>
          <span className={`text-xs px-2 py-1 rounded-full font-medium shrink-0 ${
            task.completed ? 'bg-green-100 text-green-800' :
            isOverdue ? 'bg-red-100 text-red-800' :
            task.priority === 'high' || task.priority === 'urgent' ? 'bg-orange-100 text-orange-800' : 
            task.priority === 'medium' ? 'bg-yellow-100 text-yellow-800' : 
            'bg-gray-100 text-gray-800'
          }`}>
            {task.completed ? 'Completed' : isOverdue ? 'Overdue' : task.priority}
          </span>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navigation user={user} onLogout={handleLogout} />
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#80298F]"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation user={user} onLogout={handleLogout} />
      
      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/dashboard')}
            className="flex items-center gap-2 text-gray-600 hover:text-[#80298F] mb-4 transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Dashboard
          </button>
          <h1 className="text-3xl font-heading font-bold text-gray-900">My Tasks</h1>
          <p className="text-gray-600 mt-1">View and track all your assigned tasks</p>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-3 gap-4 mb-8">
          <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm">
            <p className="text-sm text-gray-500">Overdue</p>
            <p className="text-2xl font-bold text-red-600">{overdueTasks.length}</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm">
            <p className="text-sm text-gray-500">Pending</p>
            <p className="text-2xl font-bold text-[#FAA519]">{pendingTasks.length}</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm">
            <p className="text-sm text-gray-500">Completed</p>
            <p className="text-2xl font-bold text-green-600">{completedTasks.length}</p>
          </div>
        </div>

        {/* Tasks Sections */}
        <div className="space-y-6">
          {/* Overdue Tasks Section */}
          {overdueTasks.length > 0 && (
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
              <button
                onClick={() => setOverdueCollapsed(!overdueCollapsed)}
                className="w-full flex items-center justify-between p-4 bg-red-50 hover:bg-red-100 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <AlertCircle className="h-5 w-5 text-red-600" />
                  <span className="font-semibold text-red-800">Overdue Tasks</span>
                  <span className="text-sm text-red-600 bg-red-100 px-2 py-0.5 rounded-full">
                    {overdueTasks.length}
                  </span>
                </div>
                {overdueCollapsed ? <ChevronDown className="h-5 w-5 text-red-600" /> : <ChevronUp className="h-5 w-5 text-red-600" />}
              </button>
              {!overdueCollapsed && (
                <div className="p-4 space-y-4">
                  {overdueTasks.map(task => renderTask(task))}
                </div>
              )}
            </div>
          )}

          {/* Pending Tasks Section */}
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
            <button
              onClick={() => setPendingCollapsed(!pendingCollapsed)}
              className="w-full flex items-center justify-between p-4 bg-amber-50 hover:bg-amber-100 transition-colors"
            >
              <div className="flex items-center gap-3">
                <Clock className="h-5 w-5 text-[#FAA519]" />
                <span className="font-semibold text-amber-800">Pending Tasks</span>
                <span className="text-sm text-amber-600 bg-amber-100 px-2 py-0.5 rounded-full">
                  {pendingTasks.length}
                </span>
              </div>
              {pendingCollapsed ? <ChevronDown className="h-5 w-5 text-[#FAA519]" /> : <ChevronUp className="h-5 w-5 text-[#FAA519]" />}
            </button>
            {!pendingCollapsed && (
              <div className="p-4">
                {pendingTasks.length === 0 ? (
                  <div className="text-center py-8">
                    <Clock className="h-10 w-10 text-gray-300 mx-auto mb-2" />
                    <p className="text-gray-500">No pending tasks</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {pendingTasks.map(task => renderTask(task))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Completed Tasks Section */}
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
            <button
              onClick={() => setCompletedCollapsed(!completedCollapsed)}
              className="w-full flex items-center justify-between p-4 bg-green-50 hover:bg-green-100 transition-colors"
            >
              <div className="flex items-center gap-3">
                <CheckCircle className="h-5 w-5 text-green-600" />
                <span className="font-semibold text-green-800">Completed Tasks</span>
                <span className="text-sm text-green-600 bg-green-100 px-2 py-0.5 rounded-full">
                  {completedTasks.length}
                </span>
              </div>
              {completedCollapsed ? <ChevronDown className="h-5 w-5 text-green-600" /> : <ChevronUp className="h-5 w-5 text-green-600" />}
            </button>
            {!completedCollapsed && (
              <div className="p-4">
                {completedTasks.length === 0 ? (
                  <div className="text-center py-8">
                    <CheckCircle className="h-10 w-10 text-gray-300 mx-auto mb-2" />
                    <p className="text-gray-500">No completed tasks yet</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {completedTasks.map(task => renderTask(task))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Empty State - No Tasks At All */}
        {tasks.length === 0 && (
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-12 text-center">
            <Clock className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No Tasks Assigned</h3>
            <p className="text-gray-500">You don't have any tasks assigned yet. Tasks will appear here when your legal team assigns them to you.</p>
          </div>
        )}
      </main>
    </div>
  );
}

export default MyTasks;
